<template>
    <LoadingComponent :props="loading" />

    <div class="db-card db-tab-div active">
        <div class="db-card-header border-none">
            <h3 class="db-card-title">{{ $t('label.product_categories') }}</h3>
            <div class="db-card-filter">
                <TableLimitComponent :method="list" :search="props.search" :page="paginationPage" />
                <FilterComponent v-if="paginationPage && paginationPage.total > Number(props.search.per_page || 10)" :page="pagination" :search="props.search" @click.prevent="handleSlide('product-category-filter')" />
                <div class="dropdown-group">
                    <ExportComponent />
                    <div class="dropdown-list db-card-filter-dropdown-list">
                        <PrintComponent :props="printObj" />
                        <ExcelComponent :method="xls" />
                    </div>
                </div>
                <div class="dropdown-group">
                    <ImportComponent />
                    <div class="dropdown-list db-card-filter-dropdown-list">
                        <SampleFileComponent :method="download" />
                        <UploadFileComponent props="categoryImportModal" />
                    </div>
                </div>
                <ProductCategoryCreateComponent :props="props" />
                <button
                    type="button"
                    class="db-btn h-[37px] py-2 px-3 text-white bg-primary ml-2"
                    @click="refreshFromServer"
                    :disabled="isRefreshingView"
                    :class="{ 'disabled:opacity-50 disabled:bg-gray': isRefreshingView }"
                >
                    <template v-if="isRefreshingView">
                        <svg class="inline-block animate-spin" viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                            <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="3" opacity="0.25"></circle>
                            <path d="M22 12a10 10 0 0 1-10 10" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"></path>
                        </svg>
                    </template>
                    <template v-else>
                        <i class="fa-solid fa-arrows-rotate"></i>
                    </template>
                </button>
            </div>
        </div>

        <div v-if="paginationPage && paginationPage.total > Number(props.search.per_page || 10)" class="table-filter-div" id="product-category-filter">
            <form class="p-4 sm:p-5 border-t" @submit.prevent="search">
                <div class="row pt-2">
                    <div class="col-12 sm:col-6 md:col-4 xl:col-3 !pt-0">
                        <label for="searchProductCategoryName" class="db-field-title after:hidden">{{ $t('label.name') }}</label>
                        <input id="searchProductCategoryName" v-model="props.search.name" type="text" class="db-field-control" />
                    </div>

                    <div class="col-12 sm:col-6 md:col-4 xl:col-3 !pt-0">
                        <label for="searchProductCategoryParent" class="db-field-title after:hidden">{{ $t('label.parent_category') }}</label>
                        <vue-select class="db-field-control f-b-custom-select" id="searchProductCategoryParent"
                            v-model="props.search.parent_id" :options="productCategories" label-by="name" value-by="id"
                            :closeOnSelect="true" :searchable="true" :clearOnClose="true" placeholder="--"
                            search-placeholder="--" />
                    </div>

                    <div class="col-12 sm:col-6 md:col-4 xl:col-3 !pt-0">
                        <label for="searchProductCategoryStatus" class="db-field-title after:hidden">{{ $t('label.status') }}</label>
                        <vue-select class="db-field-control f-b-custom-select" id="searchProductCategoryStatus"
                            v-model="props.search.status" :options="[
                                { id: enums.statusEnum.ACTIVE, name: $t('label.active') },
                                { id: enums.statusEnum.INACTIVE, name: $t('label.inactive') },
                            ]" label-by="name" value-by="id" :closeOnSelect="true" :searchable="true"
                            :clearOnClose="true" placeholder="--" search-placeholder="--" />
                    </div>

                    <div class="col-12 border-t mt-1">
                        <div class="flex flex-wrap justify-end gap-2">
                            <button class="db-btn py-2 px-3 bg-gray-200" @click.prevent="handleSlide('product-category-filter')">
                                <i class="lab lab-fill-close"></i>
                            </button>
                            <button class="db-btn py-2 bg-gray-200" @click="clear">
                                <span>{{ $t('button.restore') }}</span>
                            </button>
                            <button class="db-btn py-2 text-white bg-primary">
                                <span>{{ $t('button.apply_filter') }}</span>
                            </button>
                        </div>
                    </div>
                </div>
            </form>
        </div>

        <div class="db-table-responsive">
            <table class="db-table stripe" id="print">
                <thead class="db-table-head">
                    <tr class="db-table-head-tr">
                        <th class="db-table-head-th">{{ $t('label.name') }}</th>
                        <th class="db-table-head-th">{{ $t('label.parent_category') }}</th>
                        <th class="db-table-head-th">{{ $t('label.status') }}</th>
                        <th class="db-table-head-th hidden-print text-right">{{ $t('label.action') }}</th>
                    </tr>
                </thead>
                <tbody class="db-table-body" v-if="productCategories.length > 0">
                    <tr class="db-table-body-tr" v-for="productCategory in productCategories" :key="productCategory">
                        <td class="db-table-body-td">{{ productCategory.name }}</td>
                        <td class="db-table-body-td">{{ productCategory.parent_category }}</td>
                        <td class="db-table-body-td">
                            <span :class="statusClass(productCategory.status)">
                                {{ enums.statusEnumArray[productCategory.status] }}
                            </span>
                        </td>
                        <td class="db-table-body-td hidden-print">
                            <div class="flex justify-end items-center sm:items-start sm:justify-end gap-1.5">
                                <SmViewComponent :link="'admin.settings.productCategory.show'"
                                    :id="productCategory.id" />
                                <SmModalEditComponent @click="edit(productCategory)" />
                                <SmDeleteComponent @click="destroy(productCategory.id)" />
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <div class="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-6">
            <PaginationSMBox :pagination="pagination" :method="list" />
            <div class="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                <PaginationTextComponent :props="{ page: paginationPage }" />
                <PaginationBox :pagination="pagination" :method="list" />
            </div>
        </div>
    </div>
    <ProductCategoryFileUploadComponent />
</template>
<script>
import LoadingComponent from "../../components/LoadingComponent";
import ProductCategoryCreateComponent from "./ProductCategoryCreateComponent";
import alertService from "../../../../services/alertService";
import PaginationTextComponent from "../../components/pagination/PaginationTextComponent";
import PaginationBox from "../../components/pagination/PaginationBox";
import PaginationSMBox from "../../components/pagination/PaginationSMBox";
import appService from "../../../../services/appService";
import statusEnum from "../../../../enums/modules/statusEnum";
import TableLimitComponent from "../../components/TableLimitComponent";
import SmDeleteComponent from "../../components/buttons/SmDeleteComponent";
import SmModalEditComponent from "../../components/buttons/SmModalEditComponent";
import SmViewComponent from "../../components/buttons/SmViewComponent";
import ExportComponent from "../../components/buttons/export/ExportComponent";
import PrintComponent from "../../components/buttons/export/PrintComponent";
import ExcelComponent from "../../components/buttons/export/ExcelComponent";
import ImportComponent from "../../components/buttons/import/ImportComponent";
import ProductCategoryFileUploadComponent from "./ProductCategoryFileUploadComponent";
import SampleFileComponent from "../../components/buttons/import/SampleFileComponent";
import UploadFileComponent from "../../components/buttons/import/UploadFileComponent";
import FilterComponent from "../../components/buttons/collapse/FilterComponent";

export default {
    name: "ProductCategoryListComponent",
    components: {
        TableLimitComponent,
        PaginationSMBox,
        PaginationBox,
        PaginationTextComponent,
        ProductCategoryCreateComponent,
        LoadingComponent,
        SmDeleteComponent,
        SmModalEditComponent,
        SmViewComponent,
        ExportComponent,
        PrintComponent,
        ExcelComponent,
        ImportComponent,
        ProductCategoryFileUploadComponent,
        SampleFileComponent,
        UploadFileComponent,
        FilterComponent
    },
    data() {
        return {
            isRefreshingView: false,
            loading: {
                isActive: false
            },
            printObj: {
                id: "print",
                popTitle: this.$t("menu.product_categories"),
            },
            enums: {
                statusEnum: statusEnum,
                statusEnumArray: {
                    [statusEnum.ACTIVE]: this.$t("label.active"),
                    [statusEnum.INACTIVE]: this.$t("label.inactive")
                }
            },
            props: {
                form: {
                    name: "",
                    description: "",
                    parent_id: null,
                    status: statusEnum.ACTIVE,
                },
                search: {
                    paginate: 1,
                    page: 1,
                    per_page: 10,
                    order_column: 'id',
                    order_type: 'desc',
                    name: "",
                    parent_id: null,
                    status: null,
                }
            }
        }
    },
    computed: {
        productCategories: function () {
            return this.$store.getters['productCategory/lists'];
        },
        pagination: function () {
            return this.$store.getters['productCategory/pagination'];
        },
        paginationPage: function () {
            return this.$store.getters['productCategory/page'];
        }
    },
    mounted() {
        this.list();
    },
    methods: {
        refreshFromServer: function () {
            if (this.isRefreshingView) {
                return;
            }
            this.isRefreshingView = true;
            this.list().then(() => {
                alertService.info(this.$t('button.refresh'));
                this.isRefreshingView = false;
            }).catch(() => {
                this.isRefreshingView = false;
            });
        },
        statusClass: function (status) {
            return appService.statusClass(status);
        },
        textShortener: function (text, number = 30) {
            return appService.textShortener(text, number);
        },
        handleSlide: function (id) {
            return appService.handleSlide(id);
        },
        search: function () {
            this.list();
        },
        clear: function () {
            this.props.search.paginate = 1;
            this.props.search.page = 1;
            this.props.search.name = "";
            this.props.search.parent_id = null;
            this.props.search.status = null;
            this.list();
        },
        list: function (page = 1) {
            this.loading.isActive = true;
            this.props.search.page = page;

            const searchPayload = {
                ...this.props.search,
                parent_id: this.props.search.parent_id === null ? true : this.props.search.parent_id,
            };

            return this.$store.dispatch('productCategory/lists', searchPayload).then(res => {
                this.loading.isActive = false;
            }).catch((err) => {
                this.loading.isActive = false;
            });
        },
        edit: function (productCategory) {
            appService.modalShow();
            this.loading.isActive = true;
            this.$store.dispatch('productCategory/edit', productCategory.id);
            this.props.form = {
                name: productCategory.name,
                parent_id: productCategory.parent_id === 0 ? null : productCategory.parent_id,
                status: productCategory.status,
                description: productCategory.description
            };
            this.loading.isActive = false;
        },
        destroy: function (id) {
            appService.destroyConfirmation().then((res) => {
                try {
                    this.loading.isActive = true;
                    this.$store.dispatch('productCategory/destroy', { id: id, search: this.props.search }).then((res) => {
                        this.loading.isActive = false;
                        alertService.successFlip(null, this.$t('menu.product_categories'));
                    }).catch((err) => {
                        this.loading.isActive = false;
                        alertService.error(err.response.data.message);
                    })
                } catch (err) {
                    this.loading.isActive = false;
                    alertService.error(err.response.data.message);
                }
            }).catch((err) => {
                this.loading.isActive = false;
            })
        },
        xls: function () {
            this.loading.isActive = true;
            this.$store.dispatch("productCategory/export", this.props.search).then((res) => {
                this.loading.isActive = false;
                const blob = new Blob([res.data], {
                    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                });
                const link = document.createElement("a");
                link.href = URL.createObjectURL(blob);
                link.download = this.$t("menu.product_categories");
                link.click();
                URL.revokeObjectURL(link.href);
            }).catch((err) => {
                this.loading.isActive = false;
                alertService.error(err.response.data.message);
            });
        },
        download: function () {
            this.loading.isActive = true;
            this.$store.dispatch("productCategory/download").then((res) => {
                this.loading.isActive = false;
                const blob = new Blob([res.data], {
                    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                });
                const link = document.createElement("a");
                link.href = URL.createObjectURL(blob);
                link.download = "PlantillaImportacionCategorias.xlsx";
                link.click();
                URL.revokeObjectURL(link.href);
            }).catch((err) => {
                this.loading.isActive = false;
                alertService.error(err.response.data.message);
            });
        },
    }
}
</script>

<style scoped>
@media print {
    .hidden-print {
        display: none !important;
    }
}
</style>